import React from 'react'
import styles from './styles.module.scss'
import { FaDesktop, FaBook, FaUsers, FaCheck, FaTimes } from 'react-icons/fa'

const StatusBadge = ({ hasItem }) => (
  <div className={`${styles.statusBadge} ${hasItem ? styles.statusHas : styles.statusMissing}`}>
    {hasItem ? <FaCheck /> : <FaTimes />}
    <span>{hasItem ? 'имеется' : 'отсутствует'}</span>
  </div>
)

const getGradientByCompliance = (compliance) => {
  if (compliance === true) return { id: 'greenGradient', colors: ['#10B981', '#14B8A6'] }
  if (compliance === false) return { id: 'redGradient', colors: ['#F43F5F', '#E82629'] }
  return { id: 'grayGradient', colors: ['#B0B7C3', '#8D96A5'] }
}

const ProgressCircle = ({ percent, compliance }) => {
  const size = 90
  const strokeWidth = 8
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const strokeDashoffset = circumference - (Math.min(percent, 100) / 100) * circumference
  const gradient = getGradientByCompliance(compliance)
  const center = size / 2

  return (
    <div className={styles.progressCircle}>
      <svg height={size} width={size}>
        <defs>
          <linearGradient id={`${gradient.id}-${percent}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gradient.colors[0]} />
            <stop offset="100%" stopColor={gradient.colors[1]} />
          </linearGradient>
        </defs>
        <circle
          fill="transparent"
          r={radius}
          cx={center}
          cy={center}
          style={{ stroke: '#e6e6e6', strokeWidth: strokeWidth }}
        />
        <circle
          fill="transparent"
          r={radius}
          cx={center}
          cy={center}
          style={{
            stroke: `url(#${gradient.id}-${percent})`,
            strokeWidth: strokeWidth,
            strokeDasharray: `${circumference} ${circumference}`,
            strokeDashoffset: strokeDashoffset,
            strokeLinecap: 'butt',
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%'
          }}
        />
      </svg>
      <span className={styles.progressText}>{Math.round(percent)}<b>%</b></span>
    </div>
  )
}

const formatPercent = (value) => `${Number(value).toFixed(2).replace(/\.?0+$/, '')} %`

const CriterionCell = ({ item }) => {
  if (item.criterion_name) return <span className={styles.criterionText}>{item.criterion_name}</span>
  if (item.criterion === null || item.criterion === undefined) return <span>—</span>
  return <span className={styles.criterionText}>{formatPercent(item.criterion)}</span>
}

const FactCell = ({ item }) => {
  if (item.fact === null || item.fact === undefined) {
    return <span className={styles.noData}>нет данных</span>
  }

  const details = item.teachers_total !== undefined
    ? `${item.teachers_trained} из ${item.teachers_total} преп.`
    : (item.total_hours !== undefined ? `${item.fact_hours} / ${item.total_hours} ч.` : null)

  return (
    <>
      <ProgressCircle percent={item.fact} compliance={item.compliance} />
      {details && <div className={styles.factDetails}>{details}</div>}
    </>
  )
}

const CorrespondenceTable = ({ data }) => {
  if (!data) return null

  return (
    <div className={styles.tableSection}>
      <h3 className={styles.title}>Соответствие условиям реализации</h3>

      {/* Материально-техническое обеспечение */}
      <table className={styles.conditionsTable}>
        <tbody>
          <tr className={styles.categoryRow}>
            <td colSpan={2}>
              <div className={styles.categoryCell}>
                <i className={styles.categoryIcon}><FaDesktop /></i>
                <span>Материально-техническое обеспечение</span>
              </div>
            </td>
          </tr>
          <tr className={styles.itemRow}>
            <td className={styles.itemCell}>
              <p className={styles.itemText}>
                Наличие учебных аудиторий, оснащенных оборудованием и техническими средствами обучения
              </p>
              <StatusBadge hasItem={data.classroom_equipment} />
            </td>
            <td className={styles.itemCell}>
              <p className={styles.itemText}>
                Наличие помещений для самостоятельной работы обучающихся
              </p>
              <StatusBadge hasItem={data.study_rooms_available} />
            </td>
          </tr>
        </tbody>
      </table>

      {/* Учебно-методическое обеспечение */}
      <table className={styles.conditionsTable}>
        <tbody>
          <tr className={styles.categoryRow}>
            <td colSpan={5}>
              <div className={styles.categoryCell}>
                <i className={styles.categoryIcon}><FaBook /></i>
                <span>Учебно-методическое обеспечение</span>
              </div>
            </td>
          </tr>
          <tr className={styles.itemRow}>
            <td className={styles.itemCellSmall}>
              <div className={styles.itemCellContent}>
                <h4 className={styles.itemTitle}>Функционирование ЭИОС</h4>
                <p className={styles.itemDescription}>Функционирование ЭИОС у обучающихся</p>
                <StatusBadge hasItem={data.eios_functioning} />
              </div>
            </td>
            <td className={styles.itemCellSmall}>
              <div className={styles.itemCellContent}>
                <h4 className={styles.itemTitle}>Доступ к ЭИОС</h4>
                <p className={styles.itemDescription}>Индивидуальный доступ к ЭИОС у обучающихся</p>
                <StatusBadge hasItem={data.access_to_eios} />
              </div>
            </td>
            <td className={styles.itemCellSmall}>
              <div className={styles.itemCellContent}>
                <h4 className={styles.itemTitle}>Доступ к документам</h4>
                <p className={styles.itemDescription}>
                  Доступ к УП, РПД, программам практик, электронным учебным изданиям и электронным образовательным ресурсам, указанным в РПД, программах практик
                </p>
                <StatusBadge hasItem={data.access_to_docs} />
              </div>
            </td>
            <td className={styles.itemCellSmall}>
              <div className={styles.itemCellContent}>
                <h4 className={styles.itemTitle}>Электронное портфолио</h4>
                <p className={styles.itemDescription}>
                  Формирование электронного портфолио обучающегося, в том числе сохранение его работ и оценок за эти работы
                </p>
                <StatusBadge hasItem={data.electronic_portfolio} />
              </div>
            </td>
            <td className={styles.itemCellSmall}>
              <div className={styles.itemCellContent}>
                <h4 className={styles.itemTitle}>Библиотечный фонд</h4>
                <p className={styles.itemDescription}>
                  Наличие библиотечного фонда, укомплектованного печатными изданиями из расчёта не менее ___ экземпляра каждого из изданий на одного обучающегося из числа лиц, одновременно осваивающих соответствующую дисциплину (модуль), проходящих соответствующую практику
                </p>
                <StatusBadge hasItem={data.library_fund} />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Кадровое обеспечение */}
      <table className={styles.conditionsTable}>
        <tbody>
          <tr className={styles.categoryRow}>
            <td colSpan={3}>
              <div className={styles.categoryCell}>
                <i className={styles.categoryIcon}><FaUsers /></i>
                <span>Кадровое обеспечение</span>
              </div>
            </td>
          </tr>
          <tr className={styles.headerRow}>
            <th className={styles.headerCell}>Наименование требования из ФГОС</th>
            <th className={styles.headerCellCenter}>Критериальное значение</th>
            <th className={styles.headerCellCenter}>Фактическое значение</th>
          </tr>
          {data.personnel_qualifications?.map((item, index) => (
            <tr key={index} className={styles.dataRow}>
              <td className={styles.requirementCell}>{item.indicator}</td>
              <td className={styles.criterionCell}>
                <CriterionCell item={item} />
              </td>
              <td className={styles.progressCell}>
                <FactCell item={item} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default CorrespondenceTable
